import React, { useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Formik } from 'formik';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import {
  getAuth, createUserWithEmailAndPassword, sendEmailVerification,
} from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import useGlobal from 'global-state/store';
import { getEmailVerificationUrl, isChinaUser } from 'utils/firebaseProxy';
import { getFirebaseFunctions } from 'utils/firebaseChinaConfig';
import AcceptGTCU from './AcceptGTCU';

const GTCU_COOKIE_NAME = 'agreed-to-general-terms-organizations-ws';

export default function CreateAccountForm(props) {
  const {
    signinCallback = () => {},
  } = props;
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const navigate = useNavigate();
  const auth = getAuth();
  const analytics = getAnalytics();
  const app = getApp();
  const functions = getFirebaseFunctions(app);
  const [showPassword, setShowPassword] = useState(false);
  const cookies = new Cookies();
  const [acceptedGTCU, setacceptedGTCU] = useState(false);
  const [showGTCUError, setshowGTCUError] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  async function createAccount(email, password, setSubmitting) {
    if (!acceptedGTCU) {
      return setshowGTCUError(true);
    }
    try {
      const user = await createUserWithEmailAndPassword(auth, email, password);
      if (user != null) {
        // If user is on China domain, use Cloud Function to send email with China domain link
        // Note: authDomain is alcoholometry, but continueUrl should point to orgs-ws
        const isChina = isChinaUser();
        if (isChina) {
          const sendEmailVerificationChina = httpsCallable(functions, 'sendEmailVerificationChinaOrgsWs');
          await sendEmailVerificationChina({
            email: user.user.email,
          });
        } else {
          // Default behavior for non-China users
          // handleCodeInApp is not supported in Auth Emulator, so disable it in development
          const actionCodeSettings = {
            url: getEmailVerificationUrl(),
            ...(process.env.NODE_ENV === 'production' && { handleCodeInApp: true }),
          };
          await sendEmailVerification(
            user.user,
            actionCodeSettings,
          );
        }
      }
      signinCallback();
      return navigate('/');
    } catch (error) {
      globalActions.setSnackbarMessage({ message: error.message, severity: 'error' });
      logEvent(analytics, 'create_account_error', {
        error_message: error.message,
      });
    } finally {
      setSubmitting(false);
    }
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
      }}
    >

      <Formik
        initialValues={{ email: '', password: '', password2: '' }}
        validate={(values) => {
          const errors = {};
          if (!values.email) {
            errors.email = t('validations.required');
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = t('validations.email_invalid');
          }
          if (!values.password) {
            errors.password = t('validations.required');
          }
          if (values.password.length < 6) {
            errors.password = t('signin.six_char_error');
          }
          if (!values.password2) {
            errors.password2 = t('validations.required');
          }
          if (values.password2 !== values.password) {
            errors.password2 = t('signin.same_password_error');
          }
          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          // storing on both localStorage(stays longer) and cookies (sent to server) just in case
          localStorage.setItem(GTCU_COOKIE_NAME, 'true');
          cookies.set(GTCU_COOKIE_NAME, 'true');
          await createAccount(values.email, values.password, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          /* and other goodies */
        }) => (
          <form style={{ width: '100%' }} onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                width: '100%',
              }}
            >
              <TextField
                style={{ maxWidth: 300 }}
                fullWidth
                type="email"
                name="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                label={t('signin.email')}
                error={touched.email && Boolean(errors.email)}
                helperText={errors.email && touched.email && errors.email}
              />
              <TextField
                style={{ maxWidth: 300 }}
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                error={touched.password && Boolean(errors.password)}
                helperText={
                    errors.password && touched.password && errors.password
                  }
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                label={t('signin.password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                style={{ maxWidth: 300 }}
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password2"
                error={touched.password2 && Boolean(errors.password2)}
                helperText={
                    errors.password2 && touched.password2 && errors.password2
                  }
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password2}
                label={t('signin.confirm_password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <AcceptGTCU
                acceptedGTCU={acceptedGTCU}
                setacceptedGTCU={setacceptedGTCU}
                showGTCUError={showGTCUError}
                setshowGTCUError={setshowGTCUError}
              />

              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {t('signin.create_account_short')}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
}
